import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MODAL_NAMES } from "constants/modal-names";
import PropTypes from "prop-types";
import { addDays, format, isValid, parseISO } from "date-fns";
import { dateFnsFormats } from "rtr-constants";
import sharedActions from "actions/shared-actions";
import ReservationsActions from "actions/reservations-actions";
import CalendarDrawerModal from "components/source/reservations/calendar-drawer/calendar-drawer-modal";
import { useWorkingFilter } from "hooks/use-working-filter";
import { useAvailabilityFilters } from "hooks/use-availability-filters";
import Calendar from "images/design-system-svgs/calendar.svg";
import { zip_code_entry, rezo_select } from "components/source/universal_pdp/helpers/pixel-fires";
import { logReservationAction } from "helpers/reservation-pixel-helper";
import { normalizePrice } from "helpers/PricingHelper";

const propTypes = {
  date: PropTypes.string,
  duration: PropTypes.string,
  zipCode: PropTypes.string,
};

const DateZipInput = ({ date, duration, zipCode }) => {
  const dispatch = useDispatch();
  const pdpUrl = useSelector(state => state.pdpUrl);
  const product = useSelector(state => state.staticProduct);
  const reservation = useSelector(state => state.reservation);
  const [workingFiltersZipCode, setWorkingFiltersZipCode] = useWorkingFilter("zip_code", () => {});
  const { setReserveAvailability } = useAvailabilityFilters(() => {});
  const reservePricing = useSelector(state => state.pdp?.reserve?.pricing?.price);

  useEffect(() => {
    dispatch(
      ReservationsActions.updateReservation(
        {
          zipCode: zipCode ?? null,
          date: date ?? null,
          duration: duration ?? 4,
        },
        pdpUrl
      )
    );
  }, [dispatch, zipCode, date, duration, pdpUrl]);

  const formatDate = useCallback(date => format(date, `${dateFnsFormats.dayOfWeekShort} ${dateFnsFormats.M_D}`), []);

  const getFormattedDateZip = useCallback(() => {
    if (!date || !zipCode || !duration) return null;

    // Parse and validate the start date
    const beginDate = parseISO(date);
    if (!isValid(beginDate)) return null;

    // Calculate the end date
    const endDate = addDays(beginDate, duration - 1);

    // Format both dates and return the display string
    const formattedBegin = formatDate(beginDate);
    const formattedEnd = formatDate(endDate);

    return `${formattedBegin} – ${formattedEnd} in ${zipCode}`;
  }, [date, zipCode, duration, formatDate]);

  const onZipChange = zip => {
    const { zip_code } = zip;
    setWorkingFiltersZipCode(zip_code);
    zip_code_entry(zip_code);
  };

  const onSaveDates = (newDate, newDuration) => {
    setReserveAvailability(newDate, newDuration);
    rezo_select(newDate, workingFiltersZipCode);
  };

  const displayReservationCalendarModal = () => {
    dispatch(sharedActions.displayModal(MODAL_NAMES.CALENDAR_DRAWER));
    logReservationAction("rezo_search", reservation, product);
  };

  const renderModal = useCallback(() => {
    return <CalendarDrawerModal onClickSave={onSaveDates} onZipSubmit={onZipChange} zipCode={zipCode} />;
  }, [onSaveDates, onZipChange, zipCode]);

  const price = reservePricing?.find(price => price.id === "rental")?.value;
  const memberPrice = reservePricing?.find(price => price.id === "memberrental")?.value;
  const displayPrice = memberPrice || price;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <label data-test-id="hold-date-label" htmlFor="hold-date" className={`tracking-normal mb-0`}>
          <span className="font-medium capitalize">Rental Period: </span>{" "}
          <span className={`${duration ? "capitalize" : "lowercase"} text-darkGray`}>
            {duration ? `${duration} Days` : "4 or 8 days"}
          </span>
        </label>
        {duration && (
          <button className="font-medium underline" onClick={displayReservationCalendarModal}>
            {duration === "4" ? "Extend To 8 Days" : "Change To 4 Days"}
          </button>
        )}
        {!getFormattedDateZip() && (
          <div className="flex items-center gap-1 text-right">
            <span className="text-md">Starting at</span>
            {memberPrice ? <span className="text-md line-through">{normalizePrice(price)}</span> : null}
            <span className="font-semibold">{normalizePrice(displayPrice)}</span>
          </div>
        )}
      </div>

      <div className="relative">
        <div className="relative">
          <input
            id="hold-date"
            name="dateZipInput"
            className="!rounded-md border !border-darkGray !text-md"
            type="text"
            value={getFormattedDateZip() || ""}
            readOnly={true}
            onClick={displayReservationCalendarModal}
            style={{ width: "100%", cursor: "pointer" }}
            tabIndex={0}
          />
          {!getFormattedDateZip() && (
            <div className="absolute inset-0 flex space-x-16 md:space-x-24 lg:space-x-28 items-center px-3 text-darkGray pointer-events-none">
              <span>Arrival Date</span>
              <span>Return Date</span>
            </div>
          )}
          <Calendar className="absolute right-0 top-0 mt-3 mr-3 w-5 pointer-events-none" />
        </div>
        <Calendar className="absolute right-0 top-0 mt-3 mr-3 w-5 pointer-events-none" />
      </div>

      {renderModal()}
    </div>
  );
};

DateZipInput.propTypes = propTypes;

export default DateZipInput;
